import { useEffect, useState, useRef } from "react";
import { Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AssistantWidget from "@/components/AssistantWidget";
import { AssistantProvider } from "@/contexts/AssistantContext";
import { Link } from "@/components/LangLink";
import { useContent } from "@/lib/content";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";
import { getBendaggioProduct } from "@/lib/bendaggi";
import { getSchieblerProduct } from "@/lib/schiebler";

const TITLES = {
  it: {
    "/": "Medisistem | Affidabilità che si indossa",
    "/chi-siamo": "Chi siamo — Medisistem",
    "/soluzioni": "Soluzioni — Medisistem",
    "/prodotti": "Prodotti — Medisistem",
    "/prodotti/lumaven": "Calze preventive e antitrombo LUMAVEN — Medisistem",
    "/prodotti/lumaven?fam=preventive": "Calze preventive LUMAVEN — Medisistem",
    "/prodotti/lumaven?fam=antitrombo": "Calze antitrombo LUMAVEN — Medisistem",
    "/prodotti/lumaven/linea-70": "Linea 70 — Calze preventive LUMAVEN",
    "/prodotti/lumaven/linea-140": "Linea 140 — Calze preventive LUMAVEN",
    "/prodotti/lumaven/calzini-comfort": "Calzini Comfort e Comfort Forte — LUMAVEN",
    "/prodotti/lumaven/calza-antitrombo": "Calze antitrombo LUMAVEN — Medisistem",
    "/prodotti/schiebler": "Calze terapeutiche SCHIEBLER — Medisistem",
    "/prodotti/bendaggi": "Bendaggi — Medisistem",
    "/prodotti/pressoterapia": "Pressoterapia — Medisistem",
    "/il-metodo": "Il metodo Medisistem — sei passi per la misura giusta",
    "/servizi": "Servizi — Medisistem",
    "/canali": "Canali — Medisistem",
    "/contatti": "Contatti — Medisistem",
    "/privacy-policy": "Privacy Policy — Medisistem",
    "/cookie-policy": "Cookie Policy — Medisistem",
    "/termini-e-condizioni": "Condizioni d'uso — Medisistem",
  },
  en: {
    "/": "Medisistem — Elasto-Compressive Therapy since 1986",
    "/chi-siamo": "About Us — Medisistem",
    "/soluzioni": "Solutions — Medisistem",
    "/prodotti": "Products — Medisistem",
    "/prodotti/lumaven": "LUMAVEN — Medisistem",
    "/prodotti/schiebler": "SCHIEBLER — Medisistem",
    "/prodotti/bendaggi": "Bandages — Medisistem",
    "/prodotti/pressoterapia": "Pressotherapy — Medisistem",
    "/il-metodo": "The Method — Medisistem",
    "/servizi": "Services — Medisistem",
    "/canali": "Channels — Medisistem",
    "/contatti": "Contact — Medisistem",
    "/privacy-policy": "Privacy Policy — Medisistem",
    "/cookie-policy": "Cookie Policy — Medisistem",
    "/termini-e-condizioni": "Terms of Use — Medisistem",
  },
};

// Meta description per pagina (IT). Ogni pagina ha la sua: mai ripetere quella della homepage.
// Le schede prodotto ereditano la descrizione della propria categoria (vedi useEffect SEO).
const DESCRIPTIONS = {
  it: {
    "/": "Scopri Medisistem: calze e prodotti per l'elastocompressione, soluzioni per il benessere delle gambe e forniture medicali per professionisti e privati.",
    "/chi-siamo": "La storia di Medisistem: dall'esperienza del 1986 al centro specializzato nella terapia elasto-compressiva a Napoli. Affidabilità che si indossa.",
    "/soluzioni": "Cinque percorsi per il benessere delle gambe: calze terapeutiche, preventive, antitrombo, bendaggi e pressoterapia. Trova la soluzione con Medisistem.",
    "/prodotti": "Il catalogo Medisistem: calze terapeutiche SCHIEBLER, preventive e antitrombo LUMAVEN, bendaggi e pressoterapia. Filtra per esigenza e scopri le schede.",
    "/prodotti/lumaven": "La gamma LUMAVEN, marchio di proprietà Medisistem: calze preventive per il benessere quotidiano delle gambe e calze antitrombo per la profilassi.",
    "/prodotti/lumaven?fam=preventive": "Calze preventive LUMAVEN per il benessere quotidiano delle gambe: linee 70 e 140 e calzini Comfort. Misurazione professionale in sede a Napoli.",
    "/prodotti/lumaven?fam=antitrombo": "Calza antitrombo LUMAVEN 18-21 mmHg con sistema Soft Touch: gambaletto, calza alla coscia e monocollant. Guida taglie e misurazione professionale.",
    "/prodotti/lumaven/linea-70": "LUMAVEN Linea 70: calze preventive leggere per gambe riposate ogni giorno. Gambaletti, autoreggenti e collant. Scopri esecuzioni e caratteristiche.",
    "/prodotti/lumaven/linea-140": "LUMAVEN Linea 140: calze preventive a compressione più sostenuta per gambe stanche e pesanti. Scopri esecuzioni, colori e caratteristiche.",
    "/prodotti/lumaven/calzini-comfort": "I calzini preventivi LUMAVEN Comfort e Comfort Forte: benessere quotidiano per chi sta molto in piedi o viaggia. Scopri modelli e taglie.",
    "/prodotti/lumaven/calza-antitrombo": "Calza antitrombo LUMAVEN 18-21 mmHg con sistema Soft Touch: gambaletto, calza alla coscia e monocollant. Guida taglie e misurazione professionale.",
    "/prodotti/schiebler": "Calze terapeutiche SCHIEBLER, anche su misura: gambaletti, autoreggenti, collant e bracciali. Medisistem è distributore esclusivo per l'Italia.",
    "/prodotti/bendaggi": "Bendaggi per la terapia compressiva: adesivi, coesivi, non adesivi, ossido di zinco e alginato. Schede tecniche complete su Medisistem.",
    "/prodotti/pressoterapia": "Pressoterapia elettronica ADVANCE per il drenaggio linfatico sequenziale: programmi, accessori e specifiche tecniche. Scopri di più con Medisistem.",
    "/il-metodo": "Il metodo Medisistem in sei passi: ascolto, misurazione professionale e accompagnamento nella scelta della calza compressiva, in sede o a domicilio.",
    "/servizi": "I servizi Medisistem: misurazione professionale, assistenza nella scelta, servizio a domicilio e supporto continuo nella terapia elasto-compressiva.",
    "/canali": "Medisistem per farmacie, sanitarie, distributori farmaceutici, professionisti sanitari e privati: scopri i canali a cui ci rivolgiamo.",
    "/contatti": "Contatta Medisistem in Via Giuseppe Bonito 19, Napoli: prenota la misurazione in sede o a domicilio o richiedi informazioni sui prodotti.",
    "/termini-e-condizioni": "Condizioni d'uso del sito Medisistem: informazioni su contenuti, proprietà intellettuale e limitazioni di responsabilità.",
  },
};

const setLinkTag = (id, rel, href, hreflang) => {
  let el = document.head.querySelector(`link[data-i18n="${id}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("data-i18n", id);
    document.head.appendChild(el);
  }
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  if (hreflang) el.setAttribute("hreflang", hreflang);
};

const Layout = () => {
  const { lang } = useParams();
  const { pathname, hash, search } = useLocation();
  const { i18n } = useTranslation();
  const { COPY } = useContent();
  const [ctaVisible, setCtaVisible] = useState(false);
  const lastY = useRef(0);
  const upDist = useRef(0);

  const valid = SUPPORTED_LANGS.includes(lang);
  // path della pagina SENZA prefisso lingua ("/it/chi-siamo" -> "/chi-siamo", "/it" -> "/")
  const pagePath = valid ? pathname.slice(1 + lang.length) || "/" : pathname;

  useEffect(() => {
    if (!valid) return;
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    try { localStorage.setItem("ms_lang", lang); } catch { /* no-op */ }
    document.documentElement.lang = lang;
  }, [lang, valid, i18n]);

  // Titolo + SEO (canonical + hreflang it/en + x-default)
  useEffect(() => {
    if (!valid) return;
    // Viste filtrate LUMAVEN (?fam=preventive / ?fam=antitrombo): title, description e canonical dedicati
    const famQ = pagePath === "/prodotti/lumaven" ? new URLSearchParams(search).get("fam") : null;
    const famSuffix = famQ === "preventive" || famQ === "antitrombo" ? `?fam=${famQ}` : "";
    const famKey = famSuffix ? `${pagePath}${famSuffix}` : null;
    let title = (famKey && TITLES[lang] && TITLES[lang][famKey]) || (TITLES[lang] && TITLES[lang][pagePath]);
    let prodDesc = null;
    if (!title) {
      const m = pagePath.match(/^\/prodotti\/(bendaggi|schiebler)\/(.+)$/);
      if (m) {
        const p = m[1] === "bendaggi" ? getBendaggioProduct(m[2]) : getSchieblerProduct(m[2]);
        if (p) {
          title = `${p.name}${p.brand ? ` — ${p.brand}` : ""} — Medisistem`;
          if (p.title) prodDesc = `${p.name}: ${p.title}. Scheda completa su Medisistem.`;
        }
      }
    }
    document.title = title || "Medisistem";
    // Meta description per pagina; schede prodotto: tagline propria, altrimenti descrizione di categoria
    let desc = (famKey && DESCRIPTIONS.it[famKey]) || DESCRIPTIONS.it[pagePath] || prodDesc;
    if (!desc) {
      const dc = pagePath.match(/^\/prodotti\/(bendaggi|schiebler|lumaven)\//);
      if (dc) desc = DESCRIPTIONS.it[`/prodotti/${dc[1]}`];
    }
    const md = document.head.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", desc || DESCRIPTIONS.it["/"]);
    const origin = window.location.origin;
    const clean = pagePath === "/" ? "" : pagePath;
    setLinkTag("canonical", "canonical", `${origin}/${lang}${clean}${famSuffix}`);
    SUPPORTED_LANGS.forEach((l) => setLinkTag(`alt-${l}`, "alternate", `${origin}/${l}${clean}${famSuffix}`, l));
    setLinkTag("alt-default", "alternate", `${origin}/${DEFAULT_LANG}${clean}${famSuffix}`, "x-default");
  }, [lang, pagePath, valid, search]);

  // Sticky mobile CTA: appare solo dopo uno scroll verso l'alto "deciso".
  useEffect(() => {
    lastY.current = window.scrollY;
    upDist.current = 0;
    setCtaVisible(false);
    const REVEAL_THRESHOLD = 110;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      if (delta > 0) {
        upDist.current = 0;
        setCtaVisible(false);
      } else if (delta < 0) {
        upDist.current += -delta;
        if (upDist.current >= REVEAL_THRESHOLD && y > 300) setCtaVisible(true);
      }
      if (y < 300) setCtaVisible(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  useEffect(() => {
    if (hash) {
      let tries = 0;
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) { el.scrollIntoView({ behavior: "smooth" }); return true; }
        return false;
      };
      if (tryScroll()) return;
      const id = setInterval(() => {
        tries += 1;
        if (tryScroll() || tries > 12) clearInterval(id);
      }, 100);
      return () => clearInterval(id);
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  // Language guard: prefisso lingua non valido -> redirect all'italiano.
  if (!valid) {
    const isLangCode = /^[a-z]{2}$/i.test(lang || "");
    const rest = pathname.slice(1 + (lang ? lang.length : 0));
    const target = isLangCode ? (rest || "/") : pathname;
    const to = `/${DEFAULT_LANG}${target === "/" ? "" : target}${hash || ""}`;
    return <Navigate to={to} replace />;
  }

  // Normalizzazione trailing slash: /it/chi-siamo/ -> /it/chi-siamo (un solo canonical per pagina)
  if (pathname.endsWith("/")) {
    const stripped = pathname.replace(/\/+$/, "") || `/${lang}`;
    return <Navigate to={`${stripped}${search}${hash}`} replace />;
  }

  return (
    <AssistantProvider>
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />

      <AnimatePresence>
        {ctaVisible && pagePath !== "/il-metodo" && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden fixed bottom-5 inset-x-5 z-40"
          >
            <Link
              to="/il-metodo#prenota"
              data-testid="sticky-cta"
              className="btn-green w-full shadow-[0_16px_40px_-10px_rgba(46,139,107,0.7)]"
            >
              <CalendarCheck size={17} />
              {COPY.nav.ctaBooking}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      <AssistantWidget pagePath={pagePath} />
    </div>
    </AssistantProvider>
  );
};

export default Layout;
